import { useEffect, useState, useSyncExternalStore } from "react";
import {
  getDeviceSetting,
  setDeviceSetting,
  useDeviceSetting,
} from "@-/dev.improve.haniverse.lib.device-settings";
import { safeSetTimeout } from "@-/dev.improve.haniverse.lib.scheduler";

/**
 * Whether this device KNOWS another of the account's devices changed the database since its last
 * pull. A silent push (`sync-push` task) is the only writer of the signal, so a device used on its
 * own never sees one: its launches show the local replica at once and verify against the cloud in
 * the background. Two persisted counters, both read synchronously:
 *
 * - `remoteChangeSignalSeq` goes up once per received sync push.
 * - `remoteChangeCaughtUpSeq` records the signal a pull had seen when it STARTED, once that pull
 *   succeeds. A push that lands mid-pull therefore keeps the device behind until the next pull.
 *
 * A failed catch-up is remembered for this launch only, so a device that cannot reach the cloud
 * stops advertising a sync that is not happening, and the next launch tries again.
 */

/** How long the "Syncing from cloud" state may last for one signal before Start comes back anyway. */
export const REMOTE_CATCH_UP_LABEL_DEADLINE_MS = 90_000;

const failedCatchUp = { listeners: new Set<() => void>(), seq: 0 };

function subscribeFailedCatchUp(listener: () => void): () => void {
  failedCatchUp.listeners.add(listener);
  return () => {
    failedCatchUp.listeners.delete(listener);
  };
}

function getFailedCatchUpSeq(): number {
  return failedCatchUp.seq;
}

function setFailedCatchUpSeq(seq: number): void {
  failedCatchUp.seq = seq;
  for (const listener of failedCatchUp.listeners) {
    listener();
  }
}

/** Record one received sync push. Called from the background notification task. */
export function markRemoteChangeSignaled(): void {
  setDeviceSetting("remoteChangeSignalSeq", getDeviceSetting("remoteChangeSignalSeq") + 1);
}

/** The signal a pull must capture when it starts, to pass to {@link completeRemoteCatchUp}. */
export function getRemoteChangeSignalSeq(): number {
  return getDeviceSetting("remoteChangeSignalSeq");
}

export function hasPendingRemoteChange(): boolean {
  return getDeviceSetting("remoteChangeSignalSeq") > getDeviceSetting("remoteChangeCaughtUpSeq");
}

/**
 * A pull that started at `seqAtPullStart` succeeded. Returns whether it caught up on a signal this
 * device had not caught up on before, which is when the server should be told (`sync.acknowledgePush`).
 */
export function completeRemoteCatchUp(seqAtPullStart: number): boolean {
  if (seqAtPullStart <= getDeviceSetting("remoteChangeCaughtUpSeq")) {
    return false;
  }
  setDeviceSetting("remoteChangeCaughtUpSeq", seqAtPullStart);
  return true;
}

/** A pull that started at `seqAtPullStart` failed: stop showing a sync for that signal this launch. */
export function markRemoteCatchUpFailed(seqAtPullStart: number): void {
  if (seqAtPullStart > getFailedCatchUpSeq()) {
    setFailedCatchUpSeq(seqAtPullStart);
  }
}

/** Forget every signal, e.g. when the account that received them signs out. */
export function resetRemoteChangeSignal(): void {
  setDeviceSetting("remoteChangeCaughtUpSeq", getDeviceSetting("remoteChangeSignalSeq"));
  setFailedCatchUpSeq(0);
}

/**
 * True while a signed-in device knows it is behind and its catch-up pull has not failed. Bounded
 * on its own as well: whatever happens to the pull, one signal shows the state for at most
 * {@link REMOTE_CATCH_UP_LABEL_DEADLINE_MS}, so Start can never stay unavailable.
 */
export function useIsSyncingFromCloud(isSignedIn: boolean): boolean {
  const [signalSeq] = useDeviceSetting("remoteChangeSignalSeq");
  const [caughtUpSeq] = useDeviceSetting("remoteChangeCaughtUpSeq");
  const failedSeq = useSyncExternalStore(
    subscribeFailedCatchUp,
    getFailedCatchUpSeq,
    getFailedCatchUpSeq,
  );
  const isPending = isSignedIn && signalSeq > caughtUpSeq && signalSeq > failedSeq;
  const [expiredSeq, setExpiredSeq] = useState(0);
  useEffect(() => {
    if (!isPending) {
      return;
    }
    const timer = safeSetTimeout(
      () => {
        setExpiredSeq(signalSeq);
      },
      REMOTE_CATCH_UP_LABEL_DEADLINE_MS,
      "remote-catch-up-label-deadline",
    );
    return () => {
      clearTimeout(timer);
    };
  }, [isPending, signalSeq]);
  return isPending && expiredSeq < signalSeq;
}

const otherDevicePulls = { generation: 0, listeners: new Set<() => void>() };

function subscribeOtherDevicePulls(listener: () => void): () => void {
  otherDevicePulls.listeners.add(listener);
  return () => {
    otherDevicePulls.listeners.delete(listener);
  };
}

function getOtherDevicePullGeneration(): number {
  return otherDevicePulls.generation;
}

/**
 * A pull applied changes that cannot be this device's own upload coming back (it uploaded nothing
 * since its previous pull), so another device studied. The engine reports its own uploads as
 * pulled changes too, which is why the remote sync hook decides this, not the pull result.
 */
export function markOtherDeviceChangesPulled(): void {
  otherDevicePulls.generation += 1;
  for (const listener of otherDevicePulls.listeners) {
    listener();
  }
}

/** Goes up each time another device's changes are pulled in; for UI that must re-resolve. */
export function useOtherDeviceChangesPulledGeneration(): number {
  return useSyncExternalStore(
    subscribeOtherDevicePulls,
    getOtherDevicePullGeneration,
    getOtherDevicePullGeneration,
  );
}
